import { lazy, Suspense } from "react";
import { Link } from "@tanstack/react-router";
import { FAQAccordion } from "./FAQAccordion";
import { SERVICES, type ServiceSlug } from "@/lib/site";
import { WhatsAppLink } from "./WhatsAppLink";
import { messageTopicForGeoService } from "@/lib/whatsapp-message";
import type { Distrito } from "@/data/geo";

const LeadForm = lazy(() => import("./LeadForm").then((m) => ({ default: m.LeadForm })));

export interface GeoServicePageProps {
  slug: ServiceSlug;
  serviceName: string;
  distrito: Distrito;
  includes: string[];
  audience: string[];
  benefits: { title: string; desc: string }[];
}

export function GeoServicePage(p: GeoServicePageProps) {
  const h1 = `${p.serviceName} en ${p.distrito.name}, Lima`;
  // El value del select del formulario es el `name` de SERVICES, no el título geo
  const serviceOptionName = SERVICES.find((s) => s.slug === p.slug)?.name;
  const related = SERVICES.filter((s) => s.slug !== p.slug).slice(0, 3);
  const whatsappTopic = messageTopicForGeoService(p.serviceName, p.distrito.name);
  const whatsappTopicId = `${p.slug.slice(1)}-${p.distrito.slug}`;

  const faqs = [
    {
      q: `¿Ejecutan ${p.serviceName.toLowerCase()} en ${p.distrito.name}?`,
      a: `Sí. Operamos en ${p.distrito.name} y en toda Lima Metropolitana. ${p.distrito.referencia} Coordinamos producción, personal y logística desde nuestra base en Lima.`,
    },
    {
      q: `¿Cuánto cuesta ${p.serviceName.toLowerCase()} en ${p.distrito.name}?`,
      a: `El costo depende del alcance: duración, puntos de cobertura, personal y producción. Te enviamos un presupuesto desglosado en menos de 72 horas desde que recibimos el brief.`,
    },
    {
      q: `¿Cuánto tiempo de anticipación necesitan para organizar una campaña en ${p.distrito.name}?`,
      a: `Para campañas puntuales recomendamos al menos 5 días hábiles. Para proyectos con producción de materiales o personal especializado coordinamos con 2 a 3 semanas de anticipación.`,
    },
  ];

  return (
    <div className="bg-background">
      {/* HERO */}
      <section className="section-dark pt-28 pb-14 md:pt-36 md:pb-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-3xl hero-rise">
            <span className="accent-bar-diagonal inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-warm/80">
              <span className="h-1.5 w-1.5 rounded-full bg-accent shadow-[0_0_8px_var(--accent)]" />
              Lima · {p.distrito.zona} · {p.distrito.name}
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink-warm text-balance md:text-5xl lg:text-6xl">
              {h1}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-ink-warm/75 md:text-lg">
              {p.distrito.referencia} Planificamos y ejecutamos {p.serviceName.toLowerCase()} con
              personal propio, supervisión en sitio y reporte de cobertura.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/cotizar"
                className="btn-cta inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-base font-semibold"
              >
                Solicitar propuesta →
              </Link>
              <WhatsAppLink
                topic={whatsappTopic}
                topicId={whatsappTopicId}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-ink-warm/40 px-6 py-3.5 text-sm font-semibold text-ink-warm hover:border-ink-warm"
              >
                Cotizar por WhatsApp
              </WhatsAppLink>
            </div>
            <p className="mt-5 text-xs text-ink-warm/50">
              Respuesta directa del equipo · Propuesta a medida · Sin compromiso
            </p>
          </div>
        </div>
      </section>

      {/* ZONAS DE OPERACIÓN — contenido único por distrito */}
      <section className="py-14 md:py-16">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <h2 className="font-display text-2xl font-bold text-secondary md:text-3xl">
            Zonas donde operamos en {p.distrito.name}
          </h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {p.distrito.zonas.map((zona) => (
              <div
                key={zona}
                className="flex items-start gap-3 rounded-lg border border-border bg-surface px-4 py-3.5"
              >
                <span className="mt-0.5 shrink-0 text-primary">→</span>
                <span className="text-sm text-secondary/85">{zona}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUYE + PARA QUIÉN */}
      <section className="bg-surface py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 md:px-8 lg:grid-cols-2">
          <div className="rounded-lg border border-border bg-background p-7 shadow-soft">
            <h2 className="accent-bar-diagonal font-display text-2xl font-bold text-secondary md:text-3xl">
              Qué incluye
            </h2>
            <ul className="mt-5 space-y-3">
              {p.includes.map((t) => (
                <li key={t} className="flex items-start gap-3 text-secondary/85">
                  <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-md bg-accent text-accent-foreground text-[10px] font-bold">
                    ✓
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-background p-7 shadow-soft">
            <h2 className="accent-bar-diagonal font-display text-2xl font-bold text-secondary md:text-3xl">
              Para quién aplica
            </h2>
            <ul className="mt-5 space-y-3">
              {p.audience.map((t) => (
                <li key={t} className="flex items-start gap-3 text-secondary/85">
                  <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground text-[10px] font-bold">
                    →
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="bg-secondary py-16 text-secondary-foreground md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
              · Beneficios
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
              Qué ganas al trabajar con nosotros
            </h2>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {p.benefits.map((b) => (
              <div key={b.title} className="rounded-lg border border-white/10 bg-white/5 p-6">
                <h3 className="font-display text-lg font-bold">{b.title}</h3>
                <p className="mt-2 text-sm text-secondary-foreground/75">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INLINE CTA */}
      <section className="section-dark py-14 md:py-16">
        <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
          <h2 className="font-display text-2xl font-bold text-ink-warm md:text-3xl">
            ¿Tienes una campaña en {p.distrito.name}?
          </h2>
          <p className="mt-3 text-ink-warm/70">
            Te enviamos propuesta y presupuesto en menos de 72 horas.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link to="/cotizar" className="btn-cta rounded-lg px-8 py-3.5 text-base font-semibold">
              Solicitar propuesta →
            </Link>
            <WhatsAppLink
              topic={whatsappTopic}
              topicId={whatsappTopicId}
              className="inline-flex items-center gap-2 rounded-lg border border-ink-warm/40 px-6 py-3.5 text-sm font-semibold text-ink-warm hover:border-ink-warm"
            >
              Cotizar por WhatsApp
            </WhatsAppLink>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQAccordion
        items={faqs}
        title={`Preguntas sobre ${p.serviceName.toLowerCase()} en ${p.distrito.name}`}
      />

      {/* CTA FINAL + FORM */}
      <section className="py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 md:px-8 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              · Cotizar
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-secondary md:text-4xl">
              Cuéntanos tu proyecto en {p.distrito.name}. Te enviamos propuesta.
            </h2>
            <p className="mt-4 text-lg text-secondary/75">
              Completa los datos y te contactamos en menos de 24 horas hábiles. Sin compromiso.
            </p>
            <div className="mt-8">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-secondary">
                Otros servicios BTL
              </h3>
              <ul className="mt-3 flex flex-wrap gap-2">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to={r.slug}
                      className="inline-flex rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-secondary hover:border-primary hover:text-primary"
                    >
                      {r.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to={p.slug}
                    className="inline-flex rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-secondary hover:border-primary hover:text-primary"
                  >
                    ← Ver {p.serviceName}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <Suspense>
            <LeadForm defaultService={serviceOptionName} />
          </Suspense>
        </div>
      </section>
    </div>
  );
}
